import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const RAIL_H = 26;
const FADE_MS = 420;
const CROP_TOP_DEFAULT = 55;
const CROP_BOTTOM_DEFAULT = 58;
const FB_DEFAULT_SEC = 60;
const YT_FALLBACK_SEC = 240;
const FB_FAIL_MS = 5500;
const YT_FAIL_MS = 14000;

function clipKey(clip) {
  if (!clip) return "";
  if (clip.type === "facebook") return `fb:${clip.href || ""}`;
  if (clip.playlistId && !clip.id) return `pl:${clip.playlistId}`;
  return `yt:${clip.id || ""}`;
}

function ytCommand(iframe, func, args = []) {
  iframe?.contentWindow?.postMessage(
    JSON.stringify({ event: "command", func, args }),
    "*"
  );
}

function buildYoutubeSrc(id, playlistId, origin, loop) {
  const common =
    `autoplay=1&mute=1&controls=0&rel=0&modestbranding=1` +
    `&fs=0&disablekb=1&iv_load_policy=3&playsinline=1&cc_load_policy=0` +
    `&cc_lang_pref=es&enablejsapi=1&origin=${encodeURIComponent(origin)}`;
  if (playlistId && !id) {
    const loopQs = loop ? "&loop=1" : "";
    return `https://www.youtube-nocookie.com/embed/videoseries?list=${encodeURIComponent(playlistId)}&${common}${loopQs}`;
  }
  if (!id) return "";
  const loopQs = loop ? `&loop=1&playlist=${encodeURIComponent(id)}` : "";
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${common}${loopQs}`;
}

const FB_REEL_W = 267;
const FB_REEL_H = 476;

function esFacebookReel(href) {
  return /facebook\.com\/reels?\//i.test(String(href || ""));
}

function facebookReelHref(href) {
  const m = String(href || "").match(/facebook\.com\/reels?\/(\d+)/i);
  if (!m) return String(href || "").trim();
  return `https://www.facebook.com/reel/${m[1]}/`;
}

/** Iframe oficial de Insertar reel (width 267 × height 476). */
function buildFacebookEmbedSrc(href, muted) {
  if (!href) return "";
  const isReel = esFacebookReel(href);
  const canon = isReel ? facebookReelHref(href) : href;
  const params = new URLSearchParams();
  if (isReel) {
    params.set("height", String(FB_REEL_H));
    params.set("href", canon);
    params.set("show_text", "false");
    params.set("width", String(FB_REEL_W));
    params.set("t", "0");
  } else {
    params.set("href", canon);
    params.set("show_text", "false");
    params.set("width", "1280");
    params.set("height", "720");
    params.set("t", "0");
  }
  if (muted) params.set("mute", "1");
  params.set("autoplay", "true");
  return `https://www.facebook.com/plugins/video.php?${params.toString()}`;
}

function MensajeRail({ text }) {
  const msg = String(text || "").trim();
  if (!msg) return null;
  const durationSec = Math.max(14, Math.min(55, Math.round(msg.length / 2.2)));

  return (
    <div
      className="hero-yt-rail pointer-events-none absolute inset-x-0 bottom-0 z-[3] flex w-full items-center overflow-hidden border-t border-brand-accent/25 bg-[#05190f]/72 backdrop-blur-[6px]"
      style={{ height: RAIL_H, minHeight: RAIL_H }}
      role="marquee"
      aria-label={msg}
    >
      <div
        className="hero-yt-rail__track flex w-max items-center"
        style={{ animationDuration: `${durationSec}s` }}
      >
        <span className="whitespace-nowrap pl-6 pr-16 text-[11px] font-semibold leading-none tracking-[0.18em] text-white/80 uppercase sm:text-xs">
          {msg}
        </span>
        <span
          className="whitespace-nowrap pl-6 pr-16 text-[11px] font-semibold leading-none tracking-[0.18em] text-white/80 uppercase sm:text-xs"
          aria-hidden
        >
          {msg}
        </span>
      </div>
    </div>
  );
}

function parseYtMessage(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string" || !raw.startsWith("{")) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function ytEnded(data) {
  if (!data) return false;
  if (data.event === "onStateChange" && (data.info === 0 || data.info?.playerState === 0)) return true;
  if (data.info === 0 && data.event) return true;
  return false;
}

function ytPlayerState(data) {
  if (!data) return null;
  if (data.event === "onStateChange") {
    return typeof data.info === "number" ? data.info : data.info?.playerState ?? null;
  }
  if (data.event === "infoDelivery" && data.info && typeof data.info.playerState === "number") {
    return data.info.playerState;
  }
  return null;
}

function ytError(data) {
  if (!data) return false;
  if (data.event === "onError") return true;
  if (data.info && typeof data.info === "object" && data.info.error != null) return true;
  return false;
}

function facebookOrigin(origin) {
  try {
    const h = new URL(origin).hostname.replace(/^www\./, "");
    return h === "facebook.com" || h.endsWith(".facebook.com") || h === "fb.com" || h.endsWith(".fbcdn.net") || h.endsWith(".facebook.net");
  } catch {
    return false;
  }
}

function facebookPayloadText(raw) {
  if (raw == null) return "";
  if (typeof raw === "string") return raw;
  try {
    return JSON.stringify(raw);
  } catch {
    return "";
  }
}

function facebookLooksError(raw) {
  return /unavailable|not available|no est[aá] disponible|plugin_error|errorCode|cannot[\s_]play|video_id_invalid|blocked video/i.test(
    facebookPayloadText(raw)
  );
}

function facebookLooksPlaying(raw) {
  if (facebookLooksError(raw)) return false;
  const text = facebookPayloadText(raw);
  if (/startedPlaying|started_playing|finishedBuffering|video\.play|\bplaying\b|isPlaying|currentTime/i.test(text)) {
    return true;
  }
  const h =
    raw && typeof raw === "object"
      ? Number(raw.height ?? raw.payload?.height ?? raw.info?.height)
      : NaN;
  if (Number.isFinite(h) && h >= 360) return true;
  const m = text.match(/"height"\s*:\s*(\d+)/);
  return Boolean(m && Number(m[1]) >= 360);
}

function HeroMp4Fallback({ posterUrl, videoUrl, active }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (active) {
      el.muted = true;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      el.pause();
    }
    return undefined;
  }, [active, videoUrl]);

  if (!posterUrl && !videoUrl) return null;
  return (
    <video
      ref={ref}
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      poster={posterUrl || undefined}
      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
        active ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      aria-hidden={!active}
    >
      {videoUrl ? <source src={videoUrl} type="video/mp4" /> : null}
    </video>
  );
}

/**
 * Player de playlist Medios: YouTube / Facebook.
 * layout=hero: recorte a pantalla completa. layout=phone: cubre un marco 9:16 o 16:9.
 */
export default function HeroYoutubePlayer({
  clips = [],
  videoIds = [],
  facebookHref = "",
  cropTopPx = CROP_TOP_DEFAULT,
  cropBottomPx = CROP_BOTTOM_DEFAULT,
  mensajeRail = "",
  muted = true,
  volume = 80,
  captions = false,
  layout = "hero",
  frameOrientacion = "vertical",
  className = "",
  fallbackPoster = "",
  fallbackVideo = "",
  title = "Video AGDYSA",
  onClipChange,
}) {
  const hostRef = useRef(null);
  const iframeRef = useRef(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const listaKey = useMemo(
    () =>
      (Array.isArray(clips) && clips.length
        ? clips
        : facebookHref
          ? [{ type: "facebook", href: facebookHref }]
          : (videoIds || []).map((id) => ({ type: "youtube", id }))
      )
        .map((c) => `${c.type}:${c.id || c.href || c.playlistId}:${c.duracionSeg || 0}:${c.orientacion || ""}`)
        .join("|"),
    [clips, videoIds, facebookHref]
  );

  const lista = useMemo(() => {
    if (Array.isArray(clips) && clips.length) return clips;
    if (facebookHref) return [{ type: "facebook", href: facebookHref, duracionSeg: 0, playlistId: "", orientacion: "horizontal" }];
    return (videoIds || []).filter(Boolean).map((id) => ({ type: "youtube", id, duracionSeg: 0, playlistId: "", orientacion: "horizontal" }));
  }, [listaKey]);

  const [index, setIndex] = useState(0);
  const [loopTick, setLoopTick] = useState(0);
  const [visible, setVisible] = useState(true);
  const [playbackOk, setPlaybackOk] = useState(false);
  const [onlyFallback, setOnlyFallback] = useState(false);
  const advancingRef = useRef(false);
  const failedRef = useRef(new Set());
  const playbackOkRef = useRef(false);
  const skipRef = useRef(() => {});

  useEffect(() => {
    playbackOkRef.current = playbackOk;
  }, [playbackOk]);

  useEffect(() => {
    failedRef.current = new Set();
    setIndex(0);
    setPlaybackOk(false);
    setOnlyFallback(false);
  }, [listaKey]);

  const safeIndex = lista.length ? index % lista.length : 0;
  const current = lista[safeIndex] || null;
  const many = lista.length > 1;
  const isFacebook = current?.type === "facebook";
  const isPlaylist = Boolean(current?.playlistId && !current?.id);

  useEffect(() => {
    onClipChange?.(current || null);
  }, [current, onClipChange]);

  const saltarFallo = useCallback(() => {
    if (!lista.length || advancingRef.current) return;
    const key = clipKey(current);
    if (key) failedRef.current.add(key);
    const vivos = lista
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => !failedRef.current.has(clipKey(c)));
    if (!vivos.length) {
      setPlaybackOk(false);
      setOnlyFallback(true);
      return;
    }
    advancingRef.current = true;
    window.setTimeout(() => {
      advancingRef.current = false;
    }, 200);
    const siguiente = vivos.find(({ i }) => i > safeIndex) || vivos[0];
    setPlaybackOk(false);
    setIndex(siguiente.i);
  }, [lista, current, safeIndex]);

  skipRef.current = saltarFallo;

  const avanzar = useCallback(() => {
    if (!lista.length || advancingRef.current || onlyFallback) return;
    advancingRef.current = true;
    window.setTimeout(() => {
      advancingRef.current = false;
    }, FADE_MS + 80);
    if (lista.length === 1) {
      setLoopTick((n) => n + 1);
      setPlaybackOk(false);
      return;
    }
    setVisible(false);
    setPlaybackOk(false);
    window.setTimeout(() => {
      const vivos = lista
        .map((c, i) => ({ c, i }))
        .filter(({ c }) => !failedRef.current.has(clipKey(c)));
      if (!vivos.length) {
        setOnlyFallback(true);
        setVisible(true);
        return;
      }
      const siguiente = vivos.find(({ i }) => i > safeIndex) || vivos[0];
      setIndex(siguiente.i);
      setVisible(true);
    }, FADE_MS / 2);
  }, [lista, safeIndex, onlyFallback]);

  const src = useMemo(() => {
    if (!current || onlyFallback) return "";
    if (current.type === "facebook") return buildFacebookEmbedSrc(current.href, muted);
    return buildYoutubeSrc(current.id, current.playlistId, origin, !many || isPlaylist);
  }, [current, muted, origin, many, onlyFallback, isPlaylist]);

  const iframeKey = isFacebook
    ? `${safeIndex}|fb|${current?.href || ""}|${muted ? "m" : "u"}|${loopTick}`
    : `${safeIndex}|yt|${current?.id || ""}|pl:${current?.playlistId || ""}|${loopTick}`;

  useEffect(() => {
    setPlaybackOk(false);
  }, [iframeKey]);

  useEffect(() => {
    if (!current || onlyFallback || current.type !== "youtube") return undefined;
    const oembedUrl = current.playlistId && !current.id
      ? `https://www.youtube.com/playlist?list=${current.playlistId}`
      : current.id
        ? `https://www.youtube.com/watch?v=${current.id}`
        : "";
    if (!oembedUrl) return undefined;
    let cancel = false;
    const url = `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(oembedUrl)}`;
    fetch(url)
      .then((r) => {
        if (!cancel && !r.ok && !(current.playlistId && !current.id)) skipRef.current();
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [iframeKey, current, onlyFallback]);

  useEffect(() => {
    if (!current || onlyFallback) return undefined;
    const ms = current.type === "facebook" ? FB_FAIL_MS : YT_FAIL_MS;
    const t = window.setTimeout(() => {
      if (!playbackOkRef.current) skipRef.current();
    }, ms);
    return () => window.clearTimeout(t);
  }, [iframeKey, current, onlyFallback]);

  useEffect(() => {
    const host = hostRef.current;
    const iframe = iframeRef.current;
    if (!host || !iframe || onlyFallback) return undefined;

    function aplicar() {
      if (!host || !iframe) return;
      const w = host.clientWidth;
      const h = host.clientHeight;
      if (w < 8 || h < 8) return;
      const reel = isFacebook && esFacebookReel(current?.href);
      const vertical = layout === "phone" && frameOrientacion !== "horizontal";
      iframe.style.position = "absolute";
      iframe.style.border = "0";
      iframe.style.pointerEvents = "none";
      iframe.style.maxWidth = "none";
      if (layout === "phone" || reel) {
        const srcW = reel || vertical ? 1080 : 1280;
        const srcH = reel || vertical ? 1920 : 720;
        const scale = Math.max(w / srcW, h / srcH);
        const iw = srcW * scale;
        const ih = srcH * scale;
        iframe.style.width = `${iw}px`;
        iframe.style.height = `${ih}px`;
        iframe.style.left = `${(w - iw) / 2}px`;
        iframe.style.top = `${(h - ih) / 2}px`;
      } else {
        const cropTop = Math.max(0, Number(cropTopPx) || CROP_TOP_DEFAULT);
        const cropBottom = Math.max(0, Number(cropBottomPx) || CROP_BOTTOM_DEFAULT);
        iframe.style.left = "0";
        iframe.style.width = `${w}px`;
        iframe.style.top = `${-cropTop}px`;
        iframe.style.height = `${h + cropTop + cropBottom}px`;
      }
    }

    aplicar();
    const ro = new ResizeObserver(aplicar);
    ro.observe(host);
    return () => ro.disconnect();
  }, [cropTopPx, cropBottomPx, iframeKey, onlyFallback, isFacebook, current, layout, frameOrientacion]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || isFacebook || onlyFallback) return undefined;
    const apply = () => {
      const vol = Math.min(100, Math.max(0, Number(volume) || 0));
      if (muted || vol <= 0) ytCommand(iframe, "mute");
      else {
        ytCommand(iframe, "unMute");
        ytCommand(iframe, "setVolume", [vol]);
      }
      if (captions) {
        ytCommand(iframe, "loadModule", ["captions"]);
        ytCommand(iframe, "setOption", ["captions", "track", { languageCode: "es" }]);
      } else {
        ytCommand(iframe, "unloadModule", ["captions"]);
      }
    };
    apply();
    const tick = window.setInterval(apply, 1500);
    return () => window.clearInterval(tick);
  }, [muted, volume, captions, iframeKey, isFacebook, onlyFallback]);

  useEffect(() => {
    if (!current || onlyFallback) return undefined;
    const iframe = iframeRef.current;

    function onMessage(ev) {
      if (current.type === "youtube") {
        const data = parseYtMessage(ev.data);
        if (ytError(data)) {
          skipRef.current();
          return;
        }
        const st = ytPlayerState(data);
        if (st === 1 || st === 3) setPlaybackOk(true);
        if (many && !isPlaylist && ytEnded(data)) avanzar();
        return;
      }
      if (!facebookOrigin(ev.origin)) return;
      if (facebookLooksError(ev.data)) {
        skipRef.current();
        return;
      }
      if (facebookLooksPlaying(ev.data)) setPlaybackOk(true);
    }

    window.addEventListener("message", onMessage);
    const bind = window.setInterval(() => {
      if (current.type !== "youtube") return;
      iframe?.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: origin }), "*");
      ytCommand(iframe, "addEventListener", ["onStateChange"]);
      ytCommand(iframe, "addEventListener", ["onError"]);
    }, 2000);
    return () => {
      window.removeEventListener("message", onMessage);
      window.clearInterval(bind);
    };
  }, [current, many, iframeKey, origin, avanzar, onlyFallback, isPlaylist]);

  useEffect(() => {
    if (!current || onlyFallback || !playbackOk) return undefined;
    if (isPlaylist && !(Number(current.duracionSeg) > 0)) return undefined;
    const custom = Number(current.duracionSeg) || 0;
    const sec =
      custom > 0
        ? custom
        : current.type === "facebook"
          ? FB_DEFAULT_SEC
          : many
            ? YT_FALLBACK_SEC
            : 0;
    if (sec <= 0) return undefined;
    const t = window.setTimeout(() => avanzar(), sec * 1000);
    return () => window.clearTimeout(t);
  }, [current, many, iframeKey, avanzar, onlyFallback, playbackOk, isPlaylist]);

  if (!lista.length) return null;

  const showEmbed = Boolean(src) && !onlyFallback;
  const showHero = onlyFallback || !playbackOk;

  return (
    <div
      className={`relative flex h-full min-h-0 w-full flex-col ${className}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        ref={hostRef}
        className="yt-player-host relative min-h-0 w-full flex-1 overflow-hidden bg-[#05190f] transition-opacity duration-[210ms] ease-out"
        style={{ opacity: visible ? 1 : 0 }}
      >
        <HeroMp4Fallback posterUrl={fallbackPoster} videoUrl={fallbackVideo} active={showHero} />
        {showEmbed ? (
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={src}
            title={title}
            width={isFacebook && esFacebookReel(current?.href) ? FB_REEL_W : undefined}
            height={isFacebook && esFacebookReel(current?.href) ? FB_REEL_H : undefined}
            scrolling="no"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; fullscreen"
            allowFullScreen
            referrerPolicy={isFacebook ? "origin-when-cross-origin" : "strict-origin-when-cross-origin"}
            tabIndex={-1}
            className="border-0 transition-opacity duration-300"
            style={{ opacity: playbackOk ? 1 : 0 }}
          />
        ) : null}
        <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden />
        {layout === "hero" ? <MensajeRail text={mensajeRail} /> : null}
      </div>
    </div>
  );
}
